import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, Link2, Loader2, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { StepUpChallenge } from '@/features/auth/StepUpChallenge';
import { isStepUpRequired, useStepUp } from '@/features/auth/stepUp';

interface LinkedIdentity {
  id: string;
  providerId: string;
  providerName: string;
  email: string | null;
  createdAt: string;
}

interface LinkedIdentitiesResponse {
  identities: LinkedIdentity[];
  /** False when these are the only way in, so the reason can be shown up front. */
  canUnlink: boolean;
}

interface Provider {
  id: string;
  slug: string;
  displayName: string;
}

/**
 * Linking and unlinking IdP identities (`docs/WebAuthn.md` §4.8).
 *
 * Signing in through an existing link relies on a decision the user already made; *creating* the
 * link is that decision — so both actions here carry a step-up, and the server refuses without
 * one. The dialog only appears when the session's assertion has gone stale, which keeps this a
 * confirmation rather than a second hurdle.
 */
export function LinkedAccounts() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);
  const { challenge, satisfy, clear } = useStepUp();

  const { data, isLoading } = useQuery({
    queryKey: ['linkedIdentities'],
    queryFn: async () =>
      (await axios.get<LinkedIdentitiesResponse>(`${config.backendApi}/api/auth/identities`, { withCredentials: true })).data,
  });

  const { data: providers } = useQuery({
    queryKey: ['oauthProviders'],
    queryFn: async () =>
      (await axios.get<Provider[]>(`${config.backendApi}/api/auth/oauth/providers`, { withCredentials: true })).data,
  });

  // ⚠ **What to re-run once presence is re-proved.** Without this the step-up succeeded and the
  // action the user actually asked for was dropped on the floor: dialog closes, nothing happens,
  // no explanation. Held as a description rather than a closure so the retry cannot capture stale
  // state, and so the two mutations can never be confused for one another.
  const [refused, setRefused] = useState<{ kind: 'link'; providerId: string } | { kind: 'unlink'; id: string } | undefined>(
    undefined,
  );

  const link = useMutation({
    mutationFn: async (providerId: string) => {
      const { data: started } = await axios.post<{ authorizeUrl: string }>(
        `${config.backendApi}/api/auth/identities/start`,
        { providerId, returnTo: '/account/security' },
        { withCredentials: true },
      );
      // A top-level navigation, not a fetch: the IdP has to own the window, and the callback
      // comes back as a navigation too.
      window.location.assign(started.authorizeUrl);
    },
    onError: (e: unknown, providerId) => {
      const c = isStepUpRequired(e);
      if (c) { setRefused({ kind: 'link', providerId }); satisfy(c); return; }
      setError('Could not start linking. Try again.');
    },
  });

  const unlink = useMutation({
    mutationFn: (id: string) => axios.delete(`${config.backendApi}/api/auth/identities/${id}`, { withCredentials: true }),
    onSuccess: () => {
      setError(undefined);
      void queryClient.invalidateQueries({ queryKey: ['linkedIdentities'] });
    },
    onError: (e: unknown, id) => {
      const c = isStepUpRequired(e);
      if (c) { setRefused({ kind: 'unlink', id }); satisfy(c); return; }
      // The server refuses when this is the last way in, and its message names the remedy
      // ("set a password or add a passkey first") more precisely than this component could.
      const described = axios.isAxiosError(e) ? (e.response?.data as { description?: unknown } | undefined) : undefined;
      setError(
        typeof described?.description === 'string' ? described.description : 'Could not unlink that account.',
      );
    },
  });

  const linkedProviderIds = new Set((data?.identities ?? []).map((i) => i.providerId));
  const available = (providers ?? []).filter((p) => !linkedProviderIds.has(p.id));

  return (
    <section className="space-y-4" data-testid="linked-accounts">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Linked accounts</h2>
        <p className="text-sm text-muted-foreground">
          Sign in with a provider you already use. Linking asks you to confirm it is you.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {challenge && (
        <StepUpChallenge
          challenge={challenge}
          onSatisfied={() => {
            clear();
            const r = refused;
            setRefused(undefined);
            if (r?.kind === 'link') link.mutate(r.providerId);
            else if (r?.kind === 'unlink') unlink.mutate(r.id);
          }}
          // Backing out abandons the action. Re-running it here would draw a fresh refusal and
          // reopen this dialog, which is a loop with no exit.
          onCancel={() => { clear(); setRefused(undefined); }}
        />
      )}

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : data?.identities.length ? (
        <ul className="divide-y rounded-md border" data-testid="identity-list">
          {data.identities.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{i.providerName}</p>
                <p className="truncate text-xs text-muted-foreground">{i.email ?? 'No address shared'}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Unlink ${i.providerName}`}
                // Disabled rather than hidden: the reason is worth showing, and a control that
                // vanishes reads as a bug.
                disabled={!data.canUnlink || unlink.isPending}
                title={data.canUnlink ? undefined : 'This is the only way to sign in to this account.'}
                onClick={() => { setError(undefined); unlink.mutate(i.id); }}
              >
                <Unlink className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No accounts linked yet.</p>
      )}

      {available.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {available.map((p) => (
            <Button
              key={p.id}
              variant="outline"
              size="sm"
              disabled={link.isPending}
              onClick={() => { setError(undefined); link.mutate(p.id); }}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Link {p.displayName}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}
